import { useContext, useEffect, useMemo, useState } from "react"
import {
  ChevronDown,
  CheckCircle2,
  MoreHorizontal,
  X,
  Trash2,
  Filter,
  PenTool,
  ArrowLeftRightIcon,
  CornerLeftDown,
} from "lucide-react"
import { DictType } from "@pma/types"
import useTranslator from "@pma/hooks/use-translator"
import { usePermissions } from "@pma/hooks/use-permissions"
import { cn, computeBillTotal, getAccumulatedTotal, getFinancialErrorMessage, getUnsignedEarlierBills } from "@pma/utils/helpers"
import { Link } from "react-router-dom"
import {
  FINANCIAL_CONTRACTOR_COMPANIES_PAGE_ROUTE,
  PROJECTS_PAGE_ROUTE,
  USERS_PAGE_ROUTE,
} from "@pma/utils/page-routes"
import axios from "axios"
import {
  DELETE_BILL_ENDPOINT,
  FINANCIAL_SERVER_URL,
  GET_FINANCIAL_PROJECT_ROLE_ENDPOINT,
  GET_MAIN_BOQ_ENDPOINT,
  RESTORE_BILL,
} from "@pma/utils/api-routes"
import { parseCookies } from "nookies"
import { ACCESS_TOKEN } from "@pma/utils/constants"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { toast } from "@pma/hooks/use-toast"
import { Badge } from "@components/common/badge"
import { Button } from "@components/common/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@components/common/dialog"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@components/common/dropdown-menu"
import { BillDeclineDialog } from "./bill-decline-dialog"
import { BillStatusBadge } from "./bill-status-badge"
import { BulkDeclineDialog } from "./bulk-decline-bill-items"
import { BulkAdjustmentDialog } from "./bulk-adjust-bill-items"
import { Label } from "@components/common/label"
import { Switch } from "@components/common/switch"
import BoQTable from "./boq-table"
import { BulkSignDialog } from "./bulk-sign-bill-items"
import { EditBillDialog } from "./edit-bill-items"
import DeleteBillItems from "./delete-bill-items"
import { ReturnBillContext } from "./return-bill"
import ReturnBillDialog from "./return-bill"
import { useAuth } from "@pma/stores/auth-store"
import { UserRoleContext } from "@pma/contexts/UserRoleContext"
import { formatCurrency } from "@pma/utils/utils"
import { BillLifecycleStepper } from "./bill-lifecycle-stepper"
import { AccumulatedBillBreakdown } from "./accumulated-bill-breakdown"


interface Props {
  bill: DictType
  revalidate: CallableFunction
  parentExpanded: boolean
  status: "signed" | "unsigned" | "declined" | "paid" | "all" | "new"
  allBills?: DictType[]
  allBillsOnBoq?: DictType[]
}

// V2 API endpoint — uses Xano v2 API group, not the financial server
const VALIDATE_PROJECT_FULL_ENDPOINT =
  "https://xk6s-eqwg-s8by.f2.xano.io/api:z-yGa40b:v2/users/fullAccess"

export default function BillRow({ bill, revalidate, parentExpanded, status, allBills = [], allBillsOnBoq }: Props) {
  const queryClient = useQueryClient()
  const { authConfig } = useAuth()
  const { translate, locale } = useTranslator()
  const { userRoleDetails } = useContext(UserRoleContext)
  const { isOrgAdmin } = usePermissions()

  const isHebrew = locale === "he"
  const billItems = bill?._bill_items
  const loggedInUserId = authConfig?.id
  const accessToken = parseCookies()[ACCESS_TOKEN]
  const isContractorStage = bill?.status === "new"
  const isSupervisorStage = bill?.status === "pending"
  const isManagementStage = bill?.status === "approved"
  const isOrganizationOwner = authConfig?.workspace?.owner_id === authConfig?.id
  const isAdmin = isOrgAdmin
  const filterLabels: DictType = {
    all: translate("All"),
    pending: translate("Pending"),
    approved: translate("Approved"),
  }

  const [expanded, setExpanded] = useState(parentExpanded)
  const [finishApproval, setFinishApproval] = useState(false)
  const [selectedFilter, setSelectedFilter] = useState("all")
  const [showEntireBill, setShowEntireBill] = useState(false)
  const [selectedItems, setSelectedItems] = useState<any[]>([])
  const [showDeclineDialog, setShowDeclineDialog] = useState(false)
  const [showBulkEditDialog, setShowBulkEditDialog] = useState(false)
  const [showBulkSignDialog, setShowBulkSignDialog] = useState(false)
  const [filteredBillItems, setFilteredBillItems] = useState<any[]>([])
  const [showBulkAdjustDialog, setShowBulkAdjustDialog] = useState(false)
  const [showBulkDeclineDialog, setShowBulkDeclineDialog] = useState(false)
  const [showReturnDialog, setShowReturnDialog] = useState<ReturnBillContext | undefined>(undefined)
  const [showOrderWarning, setShowOrderWarning] = useState(false)
  const [pendingSignAction, setPendingSignAction] = useState<(() => void) | null>(null)

  const trySign = (action: () => void) => {
    const unsignedEarlier = getUnsignedEarlierBills(bill, allBills)
    if (unsignedEarlier.length > 0) {
      setPendingSignAction(() => action)
      setShowOrderWarning(true)
    } else {
      action()
    }
  }

  const handleFilterChange = (value: string) => {
    setShowEntireBill(false)
    let filteredItems = billItems
    if (value !== "all") {
      filteredItems = billItems.filter(
        (item: DictType) => item.approved_amount > 0 === (value === "approved"),
      )
    }
    setSelectedFilter(value)
    setFilteredBillItems(filteredItems)
  }

  const getMainBoQDetails = async () => {
    const response = await axios.post(
      FINANCIAL_SERVER_URL + GET_MAIN_BOQ_ENDPOINT,
      { boq_id: bill?.boq_id },
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
      },
    )
    return response.data
  }

  const { data: mainBoQDetails } = useQuery({
    queryKey: ["main-boq", bill?.boq_id],
    queryFn: getMainBoQDetails,
  })

  const boqItems = useMemo(() => {
    if (!mainBoQDetails?.boq_items || !bill._bill_items) return []

    const billItemsMap = new Map(
      bill._bill_items.map((billItem: any) => [billItem._boq_item.id, billItem]),
    )

    return mainBoQDetails.boq_items.map((item: any) => {
      const billItem: any = billItemsMap.get(item.id)
      if (billItem) {
        return {
          ...item,
          requested_sum: billItem?.requested_sum,
          approved_sum: billItem?.approved_sum,
        }
      }
      return item
    })
  }, [mainBoQDetails?.boq_items, bill._bill_items])

  const mergedBillAndBoqData = useMemo(() => {
    if (boqItems.length) {
      const billItemsMap = new Map(
        filteredBillItems.map((billItem: any) => [billItem._boq_item?.id, billItem]),
      )

      const merged = boqItems.map((item: any) => {
        if (item?.is_item) {
          const billItem: any = billItemsMap.get(item.id)
          if (billItem) {
            return {
              ...item,
              amount: billItem?.amount ?? item?.amount,
              approved_amount: billItem?.approved_amount ?? item?.approved_amount,
              paid: billItem?.amount_by_signer ?? item?.paid,
              requested_sum: billItem?.requested_sum,
              approved_sum: billItem?.approved_sum,
              paid_sum: billItem?.paid_sum ?? item?.paid_sum,
              item_discount: billItem?.item_discount ?? item?.item_discount,
              discount: billItem?.discount ?? item?.discount,
              bill: billItem,
            }
          }
        }
        return item
      })

      return merged.map((item: any) => {
        if (!item?.is_item && item?.number) {
          const prefix = item.number + "."
          const children = merged.filter(
            (child: any) => child?.is_item && child?.number?.startsWith(prefix),
          )
          if (children.length > 0) {
            const totalPaid = children.reduce((sum: number, c: any) => sum + (c?.paid || 0), 0)
            const totalPaidSum = children.reduce(
              (sum: number, c: any) => sum + (c?.paid_sum || 0),
              0,
            )
            return { ...item, paid: totalPaid, paid_sum: totalPaidSum }
          }
        }
        return item
      })
    }
  }, [boqItems, filteredBillItems])

  const projectId = mainBoQDetails?.boq?.project_id

  const getProjectRoles = async () => {
    const response = await axios.get(
      FINANCIAL_SERVER_URL + GET_FINANCIAL_PROJECT_ROLE_ENDPOINT + `/${projectId}`,
      { headers: { Authorization: `Bearer ${accessToken}` } },
    )

    return response.data
  }

  const { data } = useQuery({
    queryKey: ["project-roles", projectId],
    queryFn: getProjectRoles,
    enabled: !!projectId,
  })

  const getProjectAccess = async () => {
    const response = await axios.get(VALIDATE_PROJECT_FULL_ENDPOINT + `/${projectId}`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    })

    return response.data
  }

  const { data: accessData } = useQuery({
    queryKey: ["project-access", projectId],
    queryFn: getProjectAccess,
    enabled: !!projectId,
  })

  const hasFullAccess = accessData?.full_access
  const hasContractorRole = data?.find(
    (role: DictType) =>
      role?._users?.some((user: DictType) => user?.user_id == loggedInUserId) && role?.constructor,
  )

  const hasSupervisorRole = data?.find(
    (role: DictType) =>
      role?._users?.some((user: DictType) => user?.user_id == loggedInUserId) && role?.supervisor,
  )
  const hasFinanceMangerRole = data?.find(
    (role: DictType) =>
      role?._users?.some((user: DictType) => user?.user_id == loggedInUserId) && role?.fin_manager,
  )

  const deleteBill = async () => {
    const response = await axios.delete(
      FINANCIAL_SERVER_URL + DELETE_BILL_ENDPOINT + `/${bill?.id}`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
      },
    )
    return response.data
  }

  const handleDeleteBillMutation = useMutation({
    mutationKey: ["delete-boq"],
    mutationFn: deleteBill,
    onSuccess: () => {
      toast({
        title: translate("Success"),
        description: translate("bill deleted successfully"),
        variant: "success",
      })
      revalidate()
      queryClient.invalidateQueries({ queryKey: ["all-bills-summary"] })
      queryClient.invalidateQueries({ queryKey: ["main-boq-details"] })
    },
    onError: (error: DictType) => {
      const errorResponse = error?.response?.data?.message
      toast({
        title: translate("Error"),
        description: getFinancialErrorMessage(error, "delete bill", translate),
        variant: "destructive",
      })
    },
  })

  const handleItemSelection = (itemId: string, all?: boolean) => {
    if (selectedItems.includes(String(itemId)) && !all) {
      const newSelected = selectedItems.filter((item) => String(item) !== String(itemId))
      setSelectedItems(newSelected)
    } else setSelectedItems((prev) => [...prev, String(itemId)])
  }

  const resetDialog = (status: boolean) => {
    setShowBulkAdjustDialog(status)
    setShowBulkEditDialog(status)

    if (finishApproval) setSelectedItems([])

    setFinishApproval(status)
  }

  const resetBillRow = () => {
    setFinishApproval(false)
    setSelectedItems([])
    revalidate()
    queryClient.invalidateQueries({ queryKey: ["all-bills-summary"] })
    queryClient.invalidateQueries({ queryKey: ["main-boq-details"] })
  }

  const canDeleteBill =
    hasFullAccess ||
    (isContractorStage && hasContractorRole) ||
    (isSupervisorStage && hasSupervisorRole) ||
    (isManagementStage && hasFinanceMangerRole)

  useEffect(() => {
    const itemsWithRequestedAmount = bill?._bill_items.filter((item: DictType) => item?.amount)
    setFilteredBillItems(itemsWithRequestedAmount)
  }, [bill?.id, bill?._bill_items?.length])

  const restoreBill = async () => {
    const response = await axios.post(
      FINANCIAL_SERVER_URL + RESTORE_BILL,
      {
        organization_id: authConfig.last_organization_id,
        bills_id: [bill?.id],
      },
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
      },
    )
    return response.data
  }

  const restoreBillMutation = useMutation({
    mutationKey: ["restore-bill"],
    mutationFn: restoreBill,
    onSuccess: () => {
      toast({
        title: translate("Success"),
        description: translate("boq restored successfully"),
        variant: "success",
      })
      revalidate()
      queryClient.invalidateQueries({ queryKey: ["all-bills-summary"] })
      queryClient.invalidateQueries({ queryKey: ["main-boq-details"] })
    },
    onError: (error: DictType) => {
      const errorResponse = error?.response?.data?.message
      toast({
        title: translate("Error"),
        description: getFinancialErrorMessage(error, "restore bill", translate),
        variant: "error",
      })
    },
  })

  const dataList = useMemo(() => {
    const list: any[] = []
    if (filteredBillItems.length)
      filteredBillItems.map((item) =>
        list.push({
          ...item._boq_item,
          requested_sum: item?.requested_sum,
          approved_sum: item?.approved_sum,
          item_discount: item?.item_discount,
          discount_price: item?.discount_price,
          bill: { ...item, billStatus: status, fullBillStatus: bill?.status },
        }),
      )

    return list
  }, [filteredBillItems])

  const totalAccumulatedSumOfBill = useMemo(() => {
    return getAccumulatedTotal(bill, allBillsOnBoq)
  }, [bill, allBillsOnBoq])

  const showRetention =
    status === "unsigned" ||
    status === "signed" ||
    status === "paid" ||
    (status === "all" &&
      (bill?.status === "pending" || bill?.status === "approved" || bill?.status === "signed"))

  return (
    <div className='overflow-hidden rounded-md border hover:shadow-md hover:border-primary/20 transition-all duration-200'>
      {/* Header - Responsive Grid */}
      <div
        className={cn(
          "grid grid-cols-2 gap-x-4 gap-y-3 p-3 text-sm",
          "md:grid-cols-3 md:gap-x-6",
          "lg:items-center lg:gap-x-3 lg:gap-y-0 lg:px-4 lg:py-2",
          showRetention ? "lg:grid-cols-10" : "lg:grid-cols-8",
        )}
      >
        {/* Bill ID - Always visible */}
        <div className='col-span-2 flex items-center justify-between md:col-span-3 lg:col-span-1 lg:justify-start'>
          <button
            onClick={() => setExpanded(!expanded)}
            className='flex items-center focus:outline-none'
          >
            <ChevronDown
              size={20}
              className={cn(
                "h-4 w-4 text-muted-foreground transition-transform",
                expanded ? "rotate-180" : "",
                isHebrew ? "ml-1" : "mr-1",
              )}
            />
            <span className='font-semibold'>#{bill?.id}</span>
          </button>

          {/* Mobile & Tablet Actions */}
          <div className='flex items-center gap-2 lg:hidden'>
            {userRoleDetails?.fin_manager &&
              !bill?.is_signed &&
              bill?.is_approved &&
              !bill?.is_declined && (
                <Button
                  variant='outline'
                  className='border-primary text-primary hover:scale-105 active:scale-95 transition-transform'
                  size='sm'
                  onClick={() => {
                    trySign(() => {
                      setFinishApproval(true)
                      if (selectedItems.length !== bill?._bill_items.length) {
                        bill?._bill_items?.forEach((item: DictType) => {
                          setSelectedItems((prev) => [
                            ...new Set([...prev, String(item?._boq_item?.id)]),
                          ])
                        })
                      }
                      setShowBulkSignDialog(true)
                    })
                  }}
                >
                  <PenTool className='h-4 w-4' />
                </Button>
              )}

            {(userRoleDetails?.supervisor || userRoleDetails?.fin_manager) &&
              isSupervisorStage &&
              !bill?.is_approved &&
              !bill?.is_declined &&
              bill?.status !== "new" && (
                <Button
                  variant='outline'
                  className='border-purple-700 text-purple-700 hover:scale-105 active:scale-95 transition-transform'
                  onClick={() => {
                    setSelectedItems([])
                    bill?._bill_items?.forEach((item: DictType) => {
                      handleItemSelection(item?._boq_item?.id, true)
                    })
                    setFinishApproval(true)
                    setShowBulkAdjustDialog(true)
                  }}
                  size='sm'
                >
                  <CheckCircle2 className='h-4 w-4' />
                </Button>
              )}

            {isContractorStage &&
              (userRoleDetails?.constructor ||
                userRoleDetails?.supervisor ||
                userRoleDetails?.fin_manager) && (
                <Button
                  variant='outline'
                  className='border-primary text-primary hover:scale-105 active:scale-95 transition-transform'
                  onClick={() => {
                    setSelectedItems([])
                    bill?._bill_items?.forEach((item: DictType) => {
                      handleItemSelection(item?._boq_item?.id, true)
                    })
                    setFinishApproval(true)
                    setShowBulkEditDialog(true)
                  }}
                  size='sm'
                >
                  <CheckCircle2 className='h-4 w-4' />
                </Button>
              )}

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant='ghost' size='sm' className='h-8 w-8 p-0'>
                  <MoreHorizontal className='h-4 w-4' />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align='end'>
                <DropdownMenuLabel>{translate("Actions")}</DropdownMenuLabel>
                {!isContractorStage &&
                  !bill?.is_declined &&
                  !bill?.is_approved &&
                  (hasFinanceMangerRole || hasSupervisorRole || hasFullAccess) && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => setShowDeclineDialog(true)}>
                        {translate("Decline")}
                      </DropdownMenuItem>
                    </>
                  )}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className='text-destructive'
                  onClick={() => handleDeleteBillMutation.mutate()}
                  disabled={!canDeleteBill}
                >
                  {translate("Delete")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Bill Number */}
        <div className='col-span-1 lg:col-span-2'>
          <div className='flex flex-col justify-center gap-1 lg:flex-row lg:items-center lg:flex-wrap'>
            <span className='text-xs text-muted-foreground lg:hidden'>{translate("Number")}:</span>
            <span className='hidden text-xs text-muted-foreground lg:inline'>
              {translate("Number")}
            </span>
            <span className='font-semibold'>{bill?.number}</span>
            {!expanded && (
              <div className='flex flex-wrap items-center gap-2'>
                <span className='font-medium font-mono tabular-nums text-sm'>
                  {formatCurrency(computeBillTotal(bill, ["new", "pending"].includes(bill?.status)), isHebrew)}
                </span>
                <BillStatusBadge
                  status={bill?.status}
                  isApproved={bill?.is_approved}
                  isSigned={bill?.is_signed}
                  isDeclined={bill?.is_declined}
                  isReturnedToContractor={bill?.is_returned_to_contractor}
                />
                <span className='text-xs text-blue-500 font-mono'>
                  {translate("accumulated")}: {formatCurrency(
                    getAccumulatedTotal(bill, allBillsOnBoq),
                    isHebrew
                  )}
                </span>
                {bill?.is_signed && bill?.signed_at && (
                  <span className='text-xs text-muted-foreground font-mono'>
                    {translate("signed")}: {new Date(Number(bill.signed_at)).toLocaleDateString("he-IL", { day: "2-digit", month: "2-digit", year: "numeric" })}{" "}
                    {new Date(Number(bill.signed_at)).toLocaleTimeString("he-IL", { hour: "2-digit", minute: "2-digit" })}
                  </span>
                )}
                <div className='hidden lg:block w-24'>
                  <BillLifecycleStepper
                    status={bill?.status}
                    isApproved={bill?.is_approved}
                    isSigned={bill?.is_signed}
                    isDeclined={bill?.is_declined}
                    isReturnedToContractor={bill?.is_returned_to_contractor}
                    size='sm'
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Company ID */}
        <div className='col-span-1 lg:col-span-1'>
          <div className='flex flex-col gap-0.5'>
            <span className='text-xs text-muted-foreground lg:hidden'>{translate("Company")}:</span>
            <span className='hidden text-xs text-muted-foreground lg:inline'>
              {translate("Company")}
            </span>
            <Link
              to={FINANCIAL_CONTRACTOR_COMPANIES_PAGE_ROUTE + `/${bill?.contractor_company_id}`}
            >
              #<span>{bill?.contractor_company_id}</span>
            </Link>
          </div>
        </div>

        {/* Project ID */}
        <div className='col-span-1 lg:col-span-1'>
          <div className='flex flex-col gap-0.5'>
            <span className='text-xs text-muted-foreground lg:hidden'>{translate("Project")}:</span>
            <span className='hidden text-xs text-muted-foreground lg:inline'>
              {translate("Project")}
            </span>
            <Link to={PROJECTS_PAGE_ROUTE + `/${bill?.project_id}`}>
              #<span className='text-primary'>{bill?.project_id}</span>
            </Link>
          </div>
        </div>

        {/* Created By */}
        <div className='col-span-1 lg:col-span-1'>
          <div className='flex flex-col gap-0.5'>
            <span className='text-xs text-muted-foreground lg:hidden'>
              {translate("Contractor")}:
            </span>
            <span className='hidden text-xs text-muted-foreground lg:inline'>
              {translate("Contractor")}
            </span>
            <Link to={USERS_PAGE_ROUTE + `/${bill?._created_by?.id}`}>
              {bill?._created_by?.name}
            </Link>
          </div>
        </div>

        {/* Supervisor */}
        <div className='col-span-1 lg:col-span-1'>
          <div className='flex flex-col gap-0.5'>
            <span className='text-xs text-muted-foreground lg:hidden'>
              {translate("Supervisor")}:
            </span>
            <span className='hidden text-xs text-muted-foreground lg:inline'>
              {translate("Supervisor")}
            </span>
            {bill?._supervisor?.id ? (
              <Link to={USERS_PAGE_ROUTE + `/${bill?._supervisor?.id}`}>
                {bill?._supervisor?.name}
              </Link>
            ) : (
              <>---</>
            )}
          </div>
        </div>

        {/* Retention - Always visible when retention_percent > 0 */}
        {bill?.retention_percent != null && bill?.retention_percent > 0 ? (
          <div className='col-span-2 flex min-w-[120px] flex-col text-center text-xs sm:min-w-fit sm:text-sm'>
            <>
              <span className='font-medium'>
                {translate("עכבון")}: {bill?.retention_percent}%
              </span>
              <span className=''>
                {translate("sum request")}:{" "}
                {bill?.retention_sum_req != null
                  ? formatCurrency(bill?.retention_sum_req, isHebrew)
                  : "---"}
              </span>
              <span className=''>
                {translate("sum approval")}:{" "}
                {bill?.retention_sum_apr != null
                  ? formatCurrency(bill?.retention_sum_apr, isHebrew)
                  : "---"}
              </span>
            </>
          </div>
        ) : null}

        {/* Desktop Actions */}
        <div className='hidden items-center justify-end gap-2 lg:col-span-2 lg:flex'>
          {bill.deleted && (isOrganizationOwner || isAdmin || bill.deleted_by == authConfig.id) ? (
            <>
              <Button
                variant='outline'
                className='border-primary bg-blue-100 text-primary'
                size='sm'
                loading={restoreBillMutation.isPending}
                disabled={restoreBillMutation.isPending}
                onClick={() => restoreBillMutation.mutate()}
              >
                <CornerLeftDown className={cn(isHebrew ? "ml-1" : "mr-1")} size={16} />
                {translate("Restore Bill")}
              </Button>
            </>
          ) : !bill.deleted ? (
            <>
              {userRoleDetails?.fin_manager &&
                !bill?.is_signed &&
                bill?.is_approved &&
                !bill?.is_declined && (
                  <Button
                    variant='outline'
                    className='border-primary bg-blue-100 text-primary'
                    size='sm'
                    onClick={() => {
                      trySign(() => {
                        setFinishApproval(true)
                        if (selectedItems.length !== bill?._bill_items.length) {
                          bill?._bill_items?.forEach((item: DictType) => {
                            setSelectedItems((prev) => [
                              ...new Set([...prev, String(item?._boq_item?.id)]),
                            ])
                          })
                        }
                        setShowBulkSignDialog(true)
                      })
                    }}
                  >
                    <PenTool className={cn("h-4 w-4", isHebrew ? "ml-1" : "mr-1")} />
                    {translate("Sign")}
                  </Button>
                )}

              {(userRoleDetails?.supervisor || userRoleDetails?.fin_manager) &&
                isSupervisorStage &&
                !bill?.is_approved &&
                !bill?.is_declined &&
                bill?.status !== "new" && (
                  <Button
                    variant='outline'
                    className='border-purple-700 bg-purple-100 text-purple-700'
                    onClick={() => {
                      setSelectedItems([])
                      bill?._bill_items?.forEach((item: DictType) => {
                        handleItemSelection(item?._boq_item?.id, true)
                      })
                      setFinishApproval(true)
                      setShowBulkAdjustDialog(true)
                    }}
                    size='sm'
                  >
                    {translate("Finalize Approval")}
                  </Button>
                )}

              {isContractorStage &&
                (userRoleDetails?.constructor ||
                  userRoleDetails?.supervisor ||
                  userRoleDetails?.fin_manager) && (
                  <Button
                    variant='outline'
                    className='border-primary bg-blue-100 text-primary'
                    onClick={() => {
                      setSelectedItems([])
                      bill?._bill_items?.forEach((item: DictType) => {
                        handleItemSelection(item?._boq_item?.id, true)
                      })
                      setFinishApproval(true)
                      setShowBulkEditDialog(true)
                    }}
                    size='sm'
                  >
                    {translate("Send To Approval")}
                  </Button>
                )}

              {(userRoleDetails?.supervisor || userRoleDetails?.fin_manager) &&
                isSupervisorStage && (
                  <Button
                    variant='outline'
                    className='gap-1 border-error-500 text-error-500'
                    onClick={() => setShowReturnDialog("return_to_constructors")}
                    size='sm'
                  >
                    <ArrowLeftRightIcon size={14} />
                    {translate("Return")}
                  </Button>
                )}

              {userRoleDetails?.fin_manager && isManagementStage && (
                <Button
                  variant='outline'
                  className='gap-1 border-error-500 text-error-500'
                  onClick={() => setShowReturnDialog("return_to_supervisor")}
                  size='sm'
                >
                  <ArrowLeftRightIcon size={14} />
                  {translate("Return")}
                </Button>
              )}

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant='ghost' size='sm' className='h-8 w-8 p-0'>
                    <MoreHorizontal className='h-4 w-4' />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align='end'>
                  {!isContractorStage &&
                    !bill?.is_declined &&
                    !bill?.is_approved &&
                    (hasFinanceMangerRole || hasSupervisorRole || hasFullAccess) && (
                      <>
                        <DropdownMenuItem onClick={() => setShowDeclineDialog(true)}>
                          {translate("Decline")}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                      </>
                    )}
                  <DropdownMenuItem
                    className='text-destructive'
                    onClick={() => handleDeleteBillMutation.mutate()}
                    disabled={!canDeleteBill}
                  >
                    {translate("Delete")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : null}
        </div>
      </div>

      {expanded && (
        <div className='border-t bg-slate-50 p-2 lg:p-4'>
          {/* Bill Lifecycle Stepper - Medium */}
          <div className='mb-4 px-2'>
            <BillLifecycleStepper
              status={bill?.status}
              isApproved={bill?.is_approved}
              isSigned={bill?.is_signed}
              isDeclined={bill?.is_declined}
              isReturnedToContractor={bill?.is_returned_to_contractor}
              size='md'
            />
          </div>

          {/* Controls Section - Responsive */}
          <div className='mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between'>
            <h3 className='font-medium'>{translate("Bill Items")}</h3>

            {/* Selected Items Actions - Responsive */}
            {selectedItems.length > 0 && (
              <div className='flex w-full flex-1 items-center justify-center'>
                <div className='flex w-full flex-wrap items-center justify-center gap-2 rounded-md bg-slate-300 px-2 py-2 lg:bg-primary/10 lg:px-3'>
                  {bill.status === "new" && (
                    <Button
                      variant='outline'
                      className='text-primary'
                      size='sm'
                      onClick={() => setShowBulkEditDialog(true)}
                    >
                      <CheckCircle2 className={cn("h-4 w-4", isHebrew ? "ml-1" : "mr-1")} />
                      <span className='hidden sm:inline'>{translate("Edit Selected")}</span>
                      <span className='sm:hidden'>{translate("Edit")}</span> ({selectedItems.length}
                      )
                    </Button>
                  )}
                  {!bill?.is_signed && bill?.status !== "new" && (
                    <Button
                      className='bg-destructive'
                      size='sm'
                      disabled={selectedItems.length === 0}
                      onClick={() => setShowBulkDeclineDialog(true)}
                    >
                      <X className={cn("h-4 w-4", isHebrew ? "ml-1" : "mr-1")} />
                      <span className='hidden sm:inline'>
                        {translate("Decline Selected Items")}
                      </span>
                      <span className='sm:hidden'>{translate("Decline")}</span> (
                      {selectedItems.length})
                    </Button>
                  )}

                  {bill.status === "new" && (
                    <DeleteBillItems
                      bill={bill}
                      selectedItems={selectedItems}
                      setSelectedItems={setSelectedItems}
                    />
                  )}

                  <Button
                    variant='outline'
                    size='sm'
                    onClick={() => setSelectedItems([])}
                    className='border-primary text-red-700 hover:bg-red-100'
                  >
                    <Trash2 className={cn("h-4 w-4", isHebrew ? "ml-1" : "mr-1")} />
                    <span className='hidden sm:inline'>{translate("Clear")}</span>
                  </Button>
                </div>
              </div>
            )}

            {/* Filters and Switches - Responsive */}
            <div className='flex flex-wrap items-center gap-2'>
              <div className='flex items-center gap-2'>
                <Label htmlFor='entire-bill-switch' className='text-xs'>
                  {translate("Entire Bill")}
                </Label>
                <Switch
                  id='entire-bill-switch'
                  checked={showEntireBill}
                  onCheckedChange={(checked) => setShowEntireBill(checked)}
                />
              </div>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant='outline' className='gap-2 bg-transparent' size='sm'>
                    <Filter className='h-4 w-4' />
                    <span className='hidden sm:inline'>{filterLabels[selectedFilter]}</span>
                    <ChevronDown className='h-4 w-4' />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align='end' className='w-[160px]'>
                  <DropdownMenuRadioGroup value={selectedFilter} onValueChange={handleFilterChange}>
                    <DropdownMenuRadioItem value='all'>{translate("All")}</DropdownMenuRadioItem>
                    <DropdownMenuRadioItem value='pending'>
                      {translate("Pending")}
                    </DropdownMenuRadioItem>
                    <DropdownMenuRadioItem value='approved'>
                      {translate("Approved")}
                    </DropdownMenuRadioItem>
                  </DropdownMenuRadioGroup>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          {/* Table Section - Responsive with Horizontal Scroll */}
          {showEntireBill && boqItems ? (
            <BoQTable
              boqId={bill?.boq_id}
              context='bill-review'
              revalidate={revalidate}
              showCurrentBill={true}
              status={status}
              data={mergedBillAndBoqData}
            />
          ) : (
            <BoQTable
              boqId={bill?.boq_id}
              context='bill-review'
              revalidate={revalidate}
              data={dataList}
              status={status}
              showStatus={true}
            />
          )}

          {filteredBillItems?.length < 1 && (
            <div className='flex items-center justify-center p-8 text-muted-foreground'>
              {translate("No items found")}
            </div>
          )}

          {/* Bill Summary Breakdown */}
          <AccumulatedBillBreakdown
            bill={bill}
            previousBills={allBills.filter((b: DictType) => {
              if (b?.id === bill?.id) return false
              if (!b?.is_signed) return false
              const bDate = new Date(b?.signed_at || b?.created_at || 0)
              const thisDate = new Date(bill?.signed_at || bill?.created_at || 0)
              return bDate < thisDate
            })}
            retentionPercent={bill?.retention_percent || 0}
            vatPercent={bill?.vat_percent || 18}
          />
        </div>
      )}

      {/* Dialogs */}
      <BillDeclineDialog
        open={showDeclineDialog}
        onOpenChange={setShowDeclineDialog}
        billId={bill.id}
        onDecline={resetBillRow}
      />

      {showReturnDialog && (
        <ReturnBillDialog
          open={!!showReturnDialog}
          onOpenChange={setShowReturnDialog}
          bill={bill}
          revalidate={resetBillRow}
          context={showReturnDialog}
        />
      )}

      <BulkDeclineDialog
        open={showBulkDeclineDialog}
        onOpenChange={setShowBulkDeclineDialog}
        onDecline={resetBillRow}
        selectedBills={selectedItems}
        setSelectedItems={setSelectedItems}
        bill={bill}
      />

      <BulkSignDialog
        open={showBulkSignDialog}
        onOpenChange={setShowBulkSignDialog}
        onSign={resetBillRow}
        selectedBills={selectedItems}
        setSelectedItems={setSelectedItems}
        finishSign={finishApproval}
        bill={bill}
      />

      <BulkAdjustmentDialog
        open={showBulkAdjustDialog}
        onOpenChange={resetDialog}
        onAdjust={resetBillRow}
        selectedBills={selectedItems}
        setSelectedItems={setSelectedItems}
        bill={bill}
        finishApproval={finishApproval}
      />

      {showBulkEditDialog && (
        <EditBillDialog
          open={showBulkEditDialog}
          onOpenChange={resetDialog}
          onAdjust={resetBillRow}
          selectedBills={selectedItems}
          setSelectedItems={setSelectedItems}
          bill={bill}
          sendToApproval={finishApproval}
        />
      )}

      <Dialog open={showOrderWarning} onOpenChange={setShowOrderWarning}>
        <DialogContent className='sm:max-w-md' dir={isHebrew ? "rtl" : "ltr"}>
          <DialogHeader>
            <DialogTitle>{translate("Signing out of order")}</DialogTitle>
            <DialogDescription>
              {translate("The following earlier bills are not yet signed")}:
            </DialogDescription>
          </DialogHeader>
          <div className='space-y-2 py-2'>
            {getUnsignedEarlierBills(bill, allBills).map((b: DictType) => (
              <div key={b.id} className='flex items-center justify-between rounded border p-2 text-sm'>
                <span className='font-medium'>{b.number || `#${b.id}`}</span>
                <Badge variant='outline'>{translate(b.status || "unknown")}</Badge>
              </div>
            ))}
          </div>
          <DialogFooter className='gap-2'>
            <Button variant='outline' onClick={() => setShowOrderWarning(false)}>
              {translate("Cancel")}
            </Button>
            <Button
              variant='default'
              onClick={() => {
                setShowOrderWarning(false)
                if (pendingSignAction) pendingSignAction()
              }}
            >
              {translate("Sign anyway")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
// build 1775463565
